import RouteConstant from 'constants/RouteConstant'
import {isNotDefined} from 'helpers/commonHelper'
import {generateDeviceId} from 'helpers/firebaseHelper'
import crypto from 'crypto-es'

const ACCESS_TOKEN = 'access_token'

export const setAccessToken = (token: string) => {
  const aesToken = crypto.AES.encrypt(token, 'secret key').toString()
  localStorage.setItem(ACCESS_TOKEN, aesToken)
}

export const getAccessToken = (): string | null => {
  const token = localStorage.getItem(ACCESS_TOKEN)
  if (token !== null) {
    const decryptToken = crypto.AES.decrypt(token, 'secret key')
    // @ts-ignore
    return decryptToken.toString(crypto.enc.Utf8)
  }
  return null
}

export const removeAccessToken = () => localStorage.removeItem(ACCESS_TOKEN)

const REFRESH_TOKEN = 'refresh_token'

export const setRefreshToken = (token: string) => {
  const aesToken = crypto.AES.encrypt(token, 'secret refresh key').toString()
  localStorage.setItem(REFRESH_TOKEN, aesToken)
}

export const getRefreshToken = (): string | null => {
  const token = localStorage.getItem(REFRESH_TOKEN)
  if (token !== null) {
    const decryptToken = crypto.AES.decrypt(token, 'secret refresh key')
    // @ts-ignore
    return decryptToken.toString(crypto.enc.Utf8)
  }
  return null
}

export const removeRefreshToken = () => localStorage.removeItem(REFRESH_TOKEN)

const REDIRECT_FROM = 'redirect_from'

export const setRedirectFrom = (from: string) =>
  localStorage.setItem(REDIRECT_FROM, from)

export const getRedirectFrom = (): string | null =>
  localStorage.getItem(REDIRECT_FROM)

export const removeRedirectFrom = () => localStorage.removeItem(REDIRECT_FROM)

const LANGUAGE = 'lang'

const isLanguageValid = (lang: Nullable<string>): lang is LanguageCode => {
  if (isNotDefined(lang)) return false

  return lang === 'ko' || lang === 'en' || lang === 'ja' || lang === 'es'
}

export const setLanguage = (lang: Nullable<string>) => {
  if (isLanguageValid(lang)) {
    localStorage.setItem(LANGUAGE, lang)
  } else {
    console.log('잘못된 Language가 넘어왔습니다. 값을 저장하지 않습니다.')
  }
}

export const getLanguage = (): LanguageCode => {
  const lang = localStorage.getItem(LANGUAGE)
  if (isLanguageValid(lang)) {
    return lang
  }
  return 'ko'
}

const ADMIN_UID = 'admin_uid'

export const setAdminUid = (uid: string) => localStorage.setItem(ADMIN_UID, uid)

export const getAdminUid = () => localStorage.getItem(ADMIN_UID)

export const removeAdminUid = () => localStorage.removeItem(ADMIN_UID)

const USER_UID = 'user_uid'

export const setUserUid = (uid: string) => localStorage.setItem(USER_UID, uid)

export const getUserUid = () => localStorage.getItem(USER_UID)

export const removeUserUid = () => localStorage.removeItem(USER_UID)

const DEVICE_ID = 'device_id'

export const setDeviceId = (deviceId: string) =>
  localStorage.setItem(DEVICE_ID, deviceId ?? generateDeviceId(8))

export const getDeviceId = () => localStorage.getItem(DEVICE_ID)

export const getOrInitializeDeviceId = () => {
  const deviceId = getDeviceId()
  if (isNotDefined(deviceId)) {
    const newDeviceId = generateDeviceId()
    setDeviceId(newDeviceId)
    return newDeviceId
  }
  return deviceId
}

const ORGANIZATION_TYPE = 'organization_type'
const FEATURES = 'features'
const TWO_FACTOR_TYPE = 'two_factor_type'
const INPUT_TIMEOUT = 'input_timeout'

export const setOrganizationType = (uid: string) =>
  localStorage.setItem(ORGANIZATION_TYPE, uid)

export const getOrganizationType = () => localStorage.getItem(ORGANIZATION_TYPE)
export const getTwoFactorType = () => localStorage.getItem(TWO_FACTOR_TYPE)
export const getInputTimeout = () => localStorage.getItem(INPUT_TIMEOUT)

export const removeOrganizationType = () =>
  localStorage.removeItem(ORGANIZATION_TYPE)

export const removeTwoFactorType = () =>
  localStorage.removeItem(TWO_FACTOR_TYPE)

export const removeInputTimeout = () => localStorage.removeItem(INPUT_TIMEOUT)

const SELECTED_LANGUAGE = 'selected_language'

export const setLangSession = (data: string) =>
  sessionStorage.setItem(SELECTED_LANGUAGE, data)

export const getLangSession = () => sessionStorage.getItem(SELECTED_LANGUAGE)

export const removeLangSession = () =>
  sessionStorage.removeItem(SELECTED_LANGUAGE)

export const getFeatures = () => localStorage.getItem(FEATURES)
export const setFeatures = (feature: string) =>
  localStorage.setItem(FEATURES, feature)
export const setTwoFactorType = (type: string) =>
  localStorage.setItem(TWO_FACTOR_TYPE, type)

export const setInputTimeout = (time: string) =>
  localStorage.setItem(INPUT_TIMEOUT, time)

const ANALYSIS_DATE = 'analysis_date'
export const setAnalysisDate = (date: string) => {
  localStorage.setItem(ANALYSIS_DATE, date)
}
export const getAnalysisDate = () => {
  return localStorage.getItem(ANALYSIS_DATE)
}
export const removeAnalysisDate = () => localStorage.removeItem(ANALYSIS_DATE)

const IS_OFF_NOTIFICATION = 'is_off_notification'

export const setOffShowNotification = () =>
  localStorage.setItem(IS_OFF_NOTIFICATION, 'true')

export const getOffShowNotification = () =>
  localStorage.getItem(IS_OFF_NOTIFICATION)

export const removeOffShowNotification = () =>
  localStorage.removeItem(IS_OFF_NOTIFICATION)

// 로그인 공지 '일주일간 보지 않기': {noticeId: 숨김 만료 timestamp}
const HIDDEN_USER_NOTICES = 'hidden_user_notices'
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000

const getHiddenUserNotices = (): Record<string, number> =>
  JSON.parse(localStorage.getItem(HIDDEN_USER_NOTICES) ?? '{}')

export const hideUserNoticeForWeek = (noticeId: number) =>
  localStorage.setItem(
    HIDDEN_USER_NOTICES,
    JSON.stringify({
      ...getHiddenUserNotices(),
      [noticeId]: Date.now() + ONE_WEEK_MS,
    }),
  )

export const isUserNoticeHidden = (noticeId: number) =>
  (getHiddenUserNotices()[noticeId] ?? 0) > Date.now()

const IS_ON_PRICING_POLICY_NOTICE = 'is_on_pricing_policy_notice'

const IS_ON_PRICING_POLICY_KR_NOTICE = 'is_on_pricing_policy_kr_notice'

export const setPricingPolicyNotice = (value: boolean) =>
  localStorage.setItem(IS_ON_PRICING_POLICY_NOTICE, value ? 'true' : 'false')

export const isOnPricingPolicyNotice = () =>
  !localStorage.getItem(IS_ON_PRICING_POLICY_NOTICE) ||
  localStorage.getItem(IS_ON_PRICING_POLICY_NOTICE) === 'true'

export const setPricingPolicyNoticeKR = (value: boolean) =>
  localStorage.setItem(IS_ON_PRICING_POLICY_KR_NOTICE, value ? 'true' : 'false')

export const isOnPricingPolicyNoticeKR = () =>
  !localStorage.getItem(IS_ON_PRICING_POLICY_KR_NOTICE) ||
  localStorage.getItem(IS_ON_PRICING_POLICY_KR_NOTICE) === 'true'

const S3_LINKS = 's3_links'

export const setS3Link = (link: string) => localStorage.setItem(S3_LINKS, link)

export const getS3Links = () => localStorage.getItem(S3_LINKS)

export const removeS3Links = () => localStorage.removeItem(S3_LINKS)

const NOTICE_ID_KEY = 'previous_notice_id'

export const getPreviousNoticeId = (): string | null => {
  return localStorage.getItem(NOTICE_ID_KEY)
}

export const setPreviousNoticeId = (noticeId: string) => {
  localStorage.setItem(NOTICE_ID_KEY, noticeId)
}

const TIME_ICA = 'time_ica'

export const getTimeIca = (): string => {
  return localStorage.getItem(TIME_ICA) ?? ''
}

export const setTimeIca = (time: string) => {
  localStorage.setItem(TIME_ICA, time)
}
